import { CommonModule } from '@angular/common';
import {
  Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges,
} from '@angular/core';
import {
  AbstractControl, ControlContainer, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators,
} from '@angular/forms';

import { Subscription } from 'rxjs';
import { AppUtilityService } from 'src/app/app-utility.service';
import { BranchEmployerService } from 'src/app/core/services/branch-employer.service';
import {
  BasicEmployersSearchComponent,
} from 'src/app/features/branch-employers/basic-employer-form/basic-employer-form.component';
import { EmployerDetailsModel } from 'src/app/features/branch-employers/models/employer-details-model';
import { UpdateEmployerComponent } from 'src/app/features/branch-employers/update-employer/update-employer.component';
import { NumbersOnlyDirective, UniquePayslip } from 'src/app/shared/directives';
import { LMSDialogHandlerService } from 'src/app/shared/lmsdialog-handler.service';
import { MdbDatepickerModule, MdbModalService, MdbSelectModule } from 'src/lib/mdb5-adapted/public-api';

import { LMSConstantsService } from '@lmsConstants';
import { PaySlipModel1, PreAuthorisationModel } from '@lmsModels';
import { ZARcurrencyPipe } from '@lmsSharedUI';

const PERMANENTDESC = 'Permanent';

@Component({
  selector: 'app-salaried-editor',
  templateUrl: './salaried-editor.component.html',
  styleUrls: ['./salaried-editor.component.scss'],
  standalone: true,
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MdbSelectModule,
    MdbDatepickerModule,
    BasicEmployersSearchComponent,
    ZARcurrencyPipe,
    NumbersOnlyDirective,
  ],
})
export class SalariedEditorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() controlKey = '';
  parentContainer = inject(ControlContainer);
  @Input() sallabel?: string;
  @Input() Submitted = false;
  @Input() preauthModel?: PreAuthorisationModel;
  @Output() paySlipCaptured = new EventEmitter<any>();
  @Output() payslipsUpdated = new EventEmitter<PaySlipModel1[]>();
  private readonly lmsConstantsService = inject(LMSConstantsService);
  employerDetailsModel?: EmployerDetailsModel;

  updateEmployment = false;
  employmentTypes = this.lmsConstantsService.getLMSConstant('EMPLOYMENTTYPE') || [];
  wageFrequencies = this.lmsConstantsService.getLMSConstant('WAGEFREQUENCY') || [];
  payDates = this.lmsConstantsService.getLMSConstant('PAYDATES') || [];
  payDaysOfWeek = this.lmsConstantsService.getLMSConstant('PAYDAYOFWEEK') || [];
  industryTypes = this.lmsConstantsService.getLMSConstant('INDUSTRYTYPE') || [];
  lstAdditionalIncome = [
    { value: 'None', description: 'None' },
    { value: 'Rentals', description: 'Rentals' },
    { value: 'Maintanence', description: 'Maintanence' },
    { value: 'Bonus', description: 'Bonus' },
    { value: 'Dividends', description: 'Dividends' },
    { value: 'Royalties', description: 'Royalties' },
    { value: 'Other', description: 'Other' },
  ];
  originalEmployer: string;
  maxDate = new Date();
  startDate = new Date(2010, 0, 1);
  employerNametxt = '';
  payDateError = '';
  invalidDate = false;
  invalidEndDate = false;
  payslipResult: string;
  showPayslip: any;

  // Income storage for different wage frequencies
  income1 = 0;
  income2 = 0;
  income3 = 0;

  // Income arrays for Weekly and Fortnightly (stores total = nett + other)
  weeklyIncomes: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  fortnightlyIncomes: number[] = [0, 0, 0, 0, 0, 0];

  // Store full weekly and fortnightly payslips in separate arrays
  weeklyPayslips: PaySlipModel1[] = [];
  fortnightlyPayslips: PaySlipModel1[] = [];
  private additionalIncomeSub?: Subscription;
  private employmentTypeSub?: Subscription;
  private wageFrequencySub?: Subscription;
  private readonly fallbackControl = new FormControl(null);
  private readonly fallbackGroup = new FormGroup({});

  isAINoneSelected = false;
  isAIOtherOptionSelected = false;

  get showContractOrTemporary() {
    const ctype = this.f.employmentType.value ?? PERMANENTDESC;
    return ctype && ctype !== PERMANENTDESC;
  }

  get parentFormGroup() {
    return this.parentContainer.control as FormGroup;
  }

  get f() {
    return (this.parentFormGroup?.get(this.controlKey) as FormGroup)?.controls;
  }

  get totalIncome() {
    const wageFreq = this.f.wageFrequency.value;

    if (wageFreq === 'Weekly') {
      return this.weeklyIncomes.reduce((sum, income) => sum + income, 0);
    } else if (wageFreq === 'Fortnightly') {
      return this.fortnightlyIncomes.reduce((sum, income) => sum + income, 0);
    } else {
      return +this.income1 + +this.income2 + +this.income3;
    }
  }

  get isWeeklyClient() {
    return this.f.wageFrequency.value === 'Weekly';
  }

  get isFortnightlyClient() {
    return this.f.wageFrequency.value === 'Fortnightly';
  }

  get isMonthlyClient() {
    return this.f.wageFrequency.value === 'Monthly' || this.f.wageFrequency.value === 'Last day of Month';
  }

  get isLastDayOfMonthClient() {
    return this.f.wageFrequency.value === 'Last day of Month';
  }

  getWeeklyMonthTotal(month: number): number {
    const monthIndex = month - 1;
    const start = monthIndex * 4;
    return this.weeklyIncomes.slice(start, start + 4).reduce((sum, income) => sum + income, 0);
  }

  get isMonth1WeeklyComplete(): boolean {
    return this.weeklyIncomes.slice(0, 4).every((income) => income > 0);
  }

  get isMonth2WeeklyComplete(): boolean {
    return this.weeklyIncomes.slice(4, 8).every((income) => income > 0);
  }

  get isMonth3WeeklyComplete(): boolean {
    return this.weeklyIncomes.slice(8, 12).every((income) => income > 0);
  }

  getFortnightlyMonthTotal(month: number): number {
    const monthIndex = month - 1;
    const start = monthIndex * 2;
    return this.fortnightlyIncomes.slice(start, start + 2).reduce((sum, income) => sum + income, 0);
  }

  get isMonth1FortnightlyComplete(): boolean {
    return this.fortnightlyIncomes.slice(0, 2).every((income) => income > 0);
  }

  get isMonth2FortnightlyComplete(): boolean {
    return this.fortnightlyIncomes.slice(2, 4).every((income) => income > 0);
  }

  get isMonth3FortnightlyComplete(): boolean {
    return this.fortnightlyIncomes.slice(4, 6).every((income) => income > 0);
  }

  get payDateOptions() {
    return this.f?.wageFrequency?.value === 'Monthly' ? this.payDates : this.payDaysOfWeek;
  }

  get showPayDateField() {
    return this.f?.wageFrequency?.value !== 'Last day of Month';
  }

  constructor(
    private branchEmployerService: BranchEmployerService,
    public lmsDialogHandler: LMSDialogHandlerService,
    public global_utility: AppUtilityService,
    public modalService: MdbModalService,
  ) {}

  get employmentStartDate() {
    return this.f?.employmentStartDate || this.fallbackControl;
  }

  get contractStartDate() {
    return this.f?.contractStartDate || this.fallbackControl;
  }

  get contractEndDate() {
    return this.f?.contractEndDate || this.fallbackControl;
  }

  get employmentType() {
    return this.f?.employmentType || this.fallbackControl;
  }

  get wageFrequency() {
    return this.f?.wageFrequency || this.fallbackControl;
  }

  get payDate() {
    return this.f?.payDate || this.fallbackControl;
  }

  get workSaturday() {
    return this.f?.workSaturday || this.fallbackControl;
  }

  get employer() {
    return this.f?.employer || this.fallbackControl;
  }

  get payslips() {
    return (this.f?.payslips as FormGroup) || this.fallbackGroup;
  }

  get payslipControls() {
    const controls = this.payslips?.controls as
      | { [key: string]: AbstractControl<any, any> }
      | undefined;
    return {
      payslip1: controls?.payslip1 || this.fallbackControl,
      payslip2: controls?.payslip2 || this.fallbackControl,
      payslip3: controls?.payslip3 || this.fallbackControl,
    };
  }

  ngOnInit() {
    this.parentFormGroup.setControl(
      this.controlKey,
      new FormGroup(
        {
          industryType: new FormControl(null, Validators.required),
          industryTypeOther: new FormControl(''),
          employmentType: new FormControl(
            this.employmentTypes[0]?.value || '',
            Validators.required,
          ),
          employmentStartDate: new FormControl(null),
          contractStartDate: new FormControl(null),
          contractEndDate: new FormControl(null),
          employer: new FormControl(''),
          employerTNR: new FormControl(''),
          wageFrequency: new FormControl('Monthly'),
          payDate: new FormControl(''),
          occupation: new FormControl(''),
          workSaturday: new FormControl(false),
          additionalIncome: new FormControl([], Validators.required),
          additionalIncomeOther: new FormControl(''),
          payslips: new FormGroup(
            {
              payslip1: new FormControl({}),
              payslip2: new FormControl({}),
              payslip3: new FormControl({}),
            },
            { validators: UniquePayslip },
          ),
        },
        { updateOn: 'change' },
      ),
      { emitEvent: false },
    );

    this.additionalIncomeSub = this.f.additionalIncome?.valueChanges.subscribe((value) => {
      this.onAdditionalIncomeSelectionChange(value);
    });

    this.employmentTypeSub = this.f.employmentType?.valueChanges.subscribe(() => {
      this.toggleEmploymentType();
    });

    this.wageFrequencySub = this.f.wageFrequency?.valueChanges.subscribe(() => {
      this.syncPayDateValidators();
    });

    this.toggleEmploymentType();
    this.syncPayDateValidators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !(changes.preauthModel?.firstChange ?? false) &&
      Object.keys(changes.preauthModel?.currentValue ?? []).length > 0
    ) {
      setTimeout(() => {
        this.preauthModel = changes.preauthModel.currentValue;
        this.updateProfile(changes.preauthModel.currentValue);
      });
    }
  }

  updateProfile(preAuth: PreAuthorisationModel) {
    if (!preAuth || Object.values(preAuth).length == 0) {
      return;
    }

    const toDateOrDefault = (value: any, fallback: Date | null) => {
      if (!value) {
        return fallback;
      }
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? fallback : date;
    };

    const employerDetailsModel =
      preAuth.employment && preAuth.employment.id > 0 ? preAuth.employment : preAuth;
    this.employerDetailsModel = employerDetailsModel as EmployerDetailsModel;

    if (!employerDetailsModel.employmentType || employerDetailsModel.employmentType == 'SASSA') {
      employerDetailsModel.employmentType = PERMANENTDESC;
      employerDetailsModel.employerTNR = 0;
      employerDetailsModel.wageFrequency = 'Monthly';
      employerDetailsModel.workSaturday = false;
      employerDetailsModel.industryType = '';
      employerDetailsModel.occupation = '';
      employerDetailsModel.paydate = 4;
    }

    this.originalEmployer =
      preAuth.employer || preAuth.employment?.name || preAuth.employerName || '';

    this.updateEmployment = !this.originalEmployer || this.originalEmployer == 'SASSA';

    this.employerNametxt = this.originalEmployer
      ? `${this.originalEmployer},${employerDetailsModel.employerTNR || '0'}`
      : '';
    this.employer.setValue(this.employerNametxt, {
      onlySelf: false,
      emitEvent: false,
    });

    this.f.employerTNR.setValue(employerDetailsModel.employerTNR || 0, {
      onlySelf: false,
      emitEvent: false,
    });
    this.f.employmentType.setValue(employerDetailsModel.employmentType || PERMANENTDESC, {
      onlySelf: true,
      emitEvent: true,
    });

    if (employerDetailsModel.employmentType == PERMANENTDESC) {
      this.f.employmentStartDate.setValue(
        toDateOrDefault(employerDetailsModel.employmentStartDate, null),
        {
          onlySelf: false,
        },
      );
    } else {
      this.f.contractStartDate.setValue(
        toDateOrDefault(employerDetailsModel.contractStartDate, null),
        {
          onlySelf: false,
        },
      );
      this.f.contractEndDate.setValue(toDateOrDefault(employerDetailsModel.contractEndDate, null), {
        onlySelf: false,
      });
    }

    this.f.wageFrequency.setValue(
      employerDetailsModel.wageFrequency || employerDetailsModel.wageCalendar || 'Monthly',
      {
        onlySelf: false,
        emitEvent: true,
      },
    );
    this.loadIncomeData(preAuth);

    this.f.workSaturday.setValue(employerDetailsModel.workSaturday, {
      onlySelf: true,
      emitEvent: false,
    });
    this.f.occupation.setValue(employerDetailsModel.occupation, {
      onlySelf: true,
      emitEvent: false,
    });

    const industryTypeRaw = `${
      employerDetailsModel.industryType ?? preAuth.industryType ?? ''
    }`.trim();
    const industryTypeIsOther = industryTypeRaw.toLowerCase().includes('other');
    const industryTypeValue = industryTypeIsOther ? 'Other' : industryTypeRaw || null;
    const industryTypeOtherFromValue = industryTypeIsOther
      ? industryTypeRaw.split(':').slice(1).join(':').trim()
      : '';

    this.f.industryType.setValue(industryTypeValue, {
      onlySelf: true,
      emitEvent: false,
    });
    this.f.industryTypeOther.setValue(
      preAuth.industryTypeOther || industryTypeOtherFromValue || '',
      {
        onlySelf: true,
        emitEvent: false,
      },
    );

    this.toggleEmploymentType();

    const additionalIncomeRaw =
      preAuth.additionalIncome?.length == 0 ? [] : preAuth.additionalIncome;
    const additionalIncomeValue = this.fromAttribute(additionalIncomeRaw);
    const additionalIncomeArray = (
      Array.isArray(additionalIncomeValue)
        ? additionalIncomeValue
        : additionalIncomeValue
          ? [additionalIncomeValue]
          : []
    )
      .map((item: any) => `${item ?? ''}`.trim())
      .filter((item: string) => item.length > 0)
      .map((item: string) => (item.toLowerCase().includes('other') ? 'Other' : item));

    const additionalIncomeOtherFromSelection = (
      Array.isArray(additionalIncomeValue)
        ? additionalIncomeValue
        : additionalIncomeValue
          ? [additionalIncomeValue]
          : []
    )
      .map((item: any) => `${item ?? ''}`.trim())
      .find((item: string) => item.toLowerCase().includes('other'))
      ?.split(':')
      .slice(1)
      .join(':')
      .trim();

    this.f.additionalIncome?.patchValue(additionalIncomeArray, {
      onlySelf: true,
      emitEvent: false,
    });
    this.onAdditionalIncomeSelectionChange(additionalIncomeArray);

    this.f.additionalIncomeOther.setValue(
      preAuth.additionalIncomeOther || additionalIncomeOtherFromSelection || '',
      {
        onlySelf: true,
        emitEvent: false,
      },
    );

    this.paydaySelect((this.employerDetailsModel?.payDate || preAuth.payDate || '4').toString());
  }

  loadIncomeData(preAuth: PreAuthorisationModel) {
    const wageFreq = this.f.wageFrequency.value || 'Monthly';
    const currentDate = new Date();

    if (wageFreq === 'Weekly') {
      this.loadOrCalculateWeeklyIncomes(preAuth);
      return;
    }

    if (wageFreq === 'Fortnightly') {
      this.loadOrCalculateFortnightlyIncomes(preAuth);
      return;
    }

    const monthlyPayslips = [preAuth.payslip1, preAuth.payslip2, preAuth.payslip3];

    this.income1 = 0;
    this.income2 = 0;
    this.income3 = 0;

    monthlyPayslips.forEach((payslip, index) => {
      if (!payslip) {
        return;
      }

      //payslip.month = index + 1;
      if (!payslip.date && !payslip.payslipDate) {
        payslip.date = currentDate;
        payslip.payslipDate = currentDate;
      } else if (payslip.date && !payslip.payslipDate) {
        payslip.payslipDate =
          typeof payslip.date === 'string' ? new Date(payslip.date) : payslip.date;
      } else if (!payslip.date && payslip.payslipDate) {
        payslip.date =
          typeof payslip.payslipDate === 'string'
            ? new Date(payslip.payslipDate)
            : payslip.payslipDate;
      }

      const total = (+payslip.nettIncome || 0) + (+payslip.otherIncome || 0);
      if (index === 0) this.income1 = total;
      if (index === 1) this.income2 = total;
      if (index === 2) this.income3 = total;
    });
  }

  loadOrCalculateWeeklyIncomes(preAuth: PreAuthorisationModel) {
    // Clear existing data
    this.weeklyIncomes = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.weeklyPayslips = [];

    //Example of data:
    //month 1 <- Month 1 Week 1
    //month 1 <- Month 1 Week 2
    //month 1 <- Month 1 Week 3
    //month 1 <- Month 1 Week 4
    //month 2 <- Month 2 Week 1
    //month 2 <- Month 2 Week 2
    //month 2 <- Month 2 Week 3
    //month 2 <- Month 2 Week 4
    //month 3 <- Month 3 Week 1
    //month 3 <- Month 3 Week 2
    //month 3 <- Month 3 Week 3
    //month 3 <- Month 3 Week 4

    this.populatePeriodicDisplayData(
      preAuth,
      'week',
      4,
      this.weeklyIncomes,
      this.weeklyPayslips,
      () => this.convertToWeeklyIncomes(preAuth),
    );
  }

  loadOrCalculateFortnightlyIncomes(preAuth: PreAuthorisationModel) {
    // Clear existing data
    this.fortnightlyIncomes = [0, 0, 0, 0, 0, 0];
    this.fortnightlyPayslips = [];

    this.populatePeriodicDisplayData(
      preAuth,
      'fortnight',
      2,
      this.fortnightlyIncomes,
      this.fortnightlyPayslips,
      () => this.convertToFortnightlyIncomes(preAuth),
    );
  }

  private populatePeriodicDisplayData(
    preAuth: PreAuthorisationModel,
    unitKey: 'week' | 'fortnight',
    maxUnit: number,
    incomes: number[],
    payslipsStore: PaySlipModel1[],
    onFallback: () => void,
  ): void {
    if (!(preAuth.payslips && preAuth.payslips.length > 0)) {
      onFallback();
      return;
    }

    const pays = preAuth.payslips.filter((p) => {
      const month = +((p as any)?.month);
      return p && Number.isFinite(month) && month >= 1 && month <= 3;
    });

    if (pays.length === 0) {
      onFallback();
      return;
    }

    const hasUnitMarker = pays.some((p) => {
      const unit = +((p as any)?.[unitKey]);
      return Number.isFinite(unit) && unit >= 1 && unit <= maxUnit;
    });

    const monthCounts = pays.reduce((acc, p) => {
      const month = +((p as any).month) as 1 | 2 | 3;
      acc[month] += 1;
      return acc;
    }, { 1: 0, 2: 0, 3: 0 });
    const hasDetailedMonthData = monthCounts[1] > 1 || monthCounts[2] > 1 || monthCounts[3] > 1;

    if (!hasUnitMarker && !hasDetailedMonthData) {
      onFallback();
      return;
    }

    const monthSlotUsage: Record<number, number> = { 1: 0, 2: 0, 3: 0 };

    // Preserve incoming order; backend provides payslips in sequence.
    for (const payslip of pays) {
      const month = +((payslip as any).month);
      const explicitUnit = +((payslip as any)[unitKey]);
      const hasValidExplicitUnit = Number.isFinite(explicitUnit) && explicitUnit >= 1 && explicitUnit <= maxUnit;

      const unit = hasValidExplicitUnit ? explicitUnit : Math.min(monthSlotUsage[month] + 1, maxUnit);

      if (monthSlotUsage[month] >= maxUnit && !hasValidExplicitUnit) {
        continue;
      }

      const arrayIndex = ((month - 1) * maxUnit) + (unit - 1);
      incomes[arrayIndex] = (+payslip.nettIncome || 0) + (+payslip.otherIncome || 0);
      payslipsStore.push({ ...payslip, [unitKey]: unit } as any);
      monthSlotUsage[month] = Math.max(monthSlotUsage[month], unit);
    }
  }

  convertToWeeklyIncomes(preAuth: PreAuthorisationModel) {
    this.populatePeriodicFromMonthlyPayslips(preAuth, this.weeklyIncomes, 4);
  }

  convertToFortnightlyIncomes(preAuth: PreAuthorisationModel) {
    this.populatePeriodicFromMonthlyPayslips(preAuth, this.fortnightlyIncomes, 2);
  }

  private populatePeriodicFromMonthlyPayslips(
    preAuth: PreAuthorisationModel,
    incomes: number[],
    slotsPerMonth: number,
  ): void {
    const monthlyPays = [preAuth.payslip1, preAuth.payslip2, preAuth.payslip3];

    monthlyPays.forEach((payslip, monthIndex) => {
      if (!payslip) {
        return;
      }

      const monthlyIncome = (+payslip.nettIncome || 0) + (+payslip.otherIncome || 0);
      const slotIncome = monthlyIncome / slotsPerMonth;
      const startIndex = monthIndex * slotsPerMonth;

      for (let slot = 0; slot < slotsPerMonth; slot++) {
        incomes[startIndex + slot] = slotIncome;
      }
    });
  }

  public paydaySelect(payval?: any) {
    if (payval == null) {
      payval = this.payDate.value || this.employerDetailsModel?.payDate;
    }

    setTimeout(() => {
      this.f.payDate.setValue(payval, { onlySelf: true, emitEvent: true });
    }, 200);
  }

  fromAttribute(newVal) {
    return !newVal
      ? null
      : newVal.length === 0
        ? null
        : !newVal.includes(',')
          ? newVal
          : newVal.split(',').map((x) => x.trim());
  }

  toggleEmploymentType() {
    const employType = this.employmentType.value;

    if (employType == PERMANENTDESC) {
      this.employmentStartDate.enable();
      this.contractStartDate.disable();
      this.contractEndDate.disable();
      this.invalidEndDate = false;
    } else {
      this.employmentStartDate.disable();
      this.contractStartDate.enable();
      this.contractEndDate.enable();
    }

    const minemploymentStartDate = new Date();
    minemploymentStartDate.setMonth(minemploymentStartDate.getMonth() - 3);
    minemploymentStartDate.setDate(minemploymentStartDate.getDay() - 1);
    this.maxDate = minemploymentStartDate;

    this.applyEmploymentDateValidators();
  }

  private applyEmploymentDateValidators(): void {
    const isPermanentEmployment = this.employmentType.value === PERMANENTDESC;

    this.employmentStartDate.setValidators(isPermanentEmployment ? [Validators.required] : []);
    this.contractStartDate.setValidators(!isPermanentEmployment ? [Validators.required] : []);
    this.contractEndDate.setValidators(!isPermanentEmployment ? [Validators.required] : []);

    this.employmentStartDate.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.contractStartDate.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.contractEndDate.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  // Native `required` on the payDate select isn't removed by *ngIf on destroy, so validators are managed here instead.
  private syncPayDateValidators(): void {
    this.f.payDate.clearValidators();
    if (!this.isLastDayOfMonthClient) {
      this.f.payDate.setValidators([Validators.required, Validators.min(1)]);
    }
    this.f.payDate.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  togglewageFrequency() {
    this.f.payDate.setValue(null);
    this.syncPayDateValidators();
    this.f.payDate.reset();

    this.income1 = this.income2 = this.income3 = 0;
    this.weeklyIncomes = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.fortnightlyIncomes = [0, 0, 0, 0, 0, 0];
    this.weeklyPayslips = [];
    this.fortnightlyPayslips = [];

    if (this.preauthModel) {
      this.preauthModel.payslips = [];
      (this.preauthModel as any).payslip1 = null;
      (this.preauthModel as any).payslip2 = null;
      (this.preauthModel as any).payslip3 = null;
      this.preauthModel.wageCalendar = this.f.wageFrequency.value;
      this.preauthModel.wageFrequency = this.f.wageFrequency.value;
    }
  }

  PayDateSelected(event: any) {
    const selectedDate = new Date(event.target.value);
    const payMonth = 3;
    const payYear = 0;
    this.payDateError = '';
    this.startDate = selectedDate;
    const employmentStartDate = new Date();

    employmentStartDate.setFullYear(employmentStartDate.getFullYear() - payYear);
    employmentStartDate.setMonth(employmentStartDate.getMonth() - payMonth);

    this.invalidDate = selectedDate > employmentStartDate;
    if (this.invalidDate) {
      this.payDateError = 'WARNING! start date is less than 3 months';
    }
  }

  PayEndDateSelected(event: any) {
    const selectedDate = new Date(event.target.value);
    const employmentStartDate = new Date(
      this.f.contractStartDate.value || this.f.employmentStartDate.value,
    );

    this.invalidEndDate = selectedDate <= employmentStartDate;
    this.invalidDate = this.invalidEndDate;
    if (this.invalidEndDate) {
      this.payDateError = 'End Date must be after Start Date';
    }
  }

  goforNew() {
    this.branchEmployerService.preppedforCreate = true;

    const dialogRef = this.modalService.open(UpdateEmployerComponent, {
      ignoreBackdropClick: true,
      modalClass: 'modal-xl modal-dialog-centered modal-dialog-scrollable update-employer-modal',
      data: {
        employer: new EmployerDetailsModel(),
      },
    });

    dialogRef.onClose.subscribe((employer: any) => {
      if (employer) {
        this.doEmployerConfirm(employer);
        return true;
      }

      this.global_utility.warning('Employment details not saved', 'Incomplete employment');
      return false;
    });
  }

  doEmployerConfirm(event: any) {
    const incomingTnr = Number(event?.tnumber || 0);
    const incomingName = event?.name || event?.employerName || '';

    const employersSearch = {
      employerName: incomingName,
      name: incomingName,
      employerTNR: incomingTnr,
      branchSerial: this.global_utility.getGlobalData('branch_serial'),
    };

    const employerpromise = new Promise<boolean>((resolve) => {
      if ((!incomingTnr || incomingTnr == 0) && employersSearch.name === '') {
        resolve(false);
        return;
      }

      this.branchEmployerService.findEmployerBySerialTNR(employersSearch).subscribe({
        next: (employerList: any) => {
          this.employerDetailsModel = employerList;

          this.updateEmployment = false;
          if (
            this.originalEmployer == '' ||
            this.originalEmployer != this.employerDetailsModel?.name
          ) {
            this.updateEmployment = true;
          }

          const resolvedTnr = Number(
            employerList?.employerTNR ??
              employerList?.tnumber ??
              employerList?.id ??
              incomingTnr ??
              0,
          );
          const foundemployer = resolvedTnr > 0;
          resolve(foundemployer);
        },
        error: () => {
          resolve(false);
        },
      });
    });

    employerpromise
      .then((employerselected) => {
        if (employerselected) {
          const resolvedTnr = Number(
            event?.tnumber ??
              this.employerDetailsModel?.employerTNR ??
              this.employerDetailsModel?.id ??
              0,
          );
          const employerDisplayName =
            incomingName ||
            this.employerDetailsModel?.name ||
            this.employerDetailsModel?.employerName ||
            this.branchEmployerService.employerDetailsModel?.employerName ||
            '';

          this.employerNametxt = employerDisplayName;

          const currentEmployerName = (this.employerNametxt ?? '').toString().trim().toLowerCase();
          if (currentEmployerName.startsWith('sassa')) {
            this.global_utility.error(
              'Client type is set to SALARIED, with employer as SASSA! This is not currently permitted!<br/><br/> Please select the correct client type/employment combination.',
              'Invalid employer',
            );
            this.f.employer.setErrors({ invalidEmployer: true }, { emitEvent: true });
            return;
          }

          this.f.employmentType.setValue(this.preauthModel!.employmentType || PERMANENTDESC, {
            onlySelf: false,
            emitEvent: true,
          });
          this.f.employer.setValue(employerDisplayName, { onlySelf: true, emitEvent: false });
          this.f.employerTNR.setValue(resolvedTnr, {
            onlySelf: true,
            emitEvent: true,
          });
          if (this.preauthModel!.employmentType === PERMANENTDESC) {
            this.f.employmentStartDate.setValue(this.preauthModel!.employment.employmentStartDate, {
              onlySelf: false,
              emitEvent: true,
            });
          } else {
            this.f.contractStartDate.setValue(this.preauthModel!.employment.contractStartDate, {
              onlySelf: false,
              emitEvent: true,
            });
            this.f.contractEndDate.setValue(this.preauthModel!.employment.contractEndDate, {
              onlySelf: false,
              emitEvent: true,
            });
          }
          this.f.wageFrequency.setValue(
            this.employerDetailsModel?.luEmpPayFrequency || this.preauthModel!.wageCalendar || '',
            { onlySelf: true, emitEvent: false },
          );
          this.f.industryType.setValue(this.preauthModel!.employment.industryType, {
            onlySelf: true,
            emitEvent: false,
          });
          this.f.industryTypeOther.setValue(this.preauthModel!.employment.industryTypeOther, {
            onlySelf: true,
            emitEvent: false,
          });
          this.f.payDate.setValue(this.employerDetailsModel!.payDate.toString() || '4', {
            onlySelf: true,
            emitEvent: true,
          });
          this.toggleEmploymentType();
        }
      })
      .catch((reason) => {
        console.error('Salaried employer error:', reason);
      });
  }

  openPaySlip(mnth: string | number) {
    const month = Number(mnth);
    let pslipModel = {} as PaySlipModel1;

    if (month === 1) {
      pslipModel = this.preauthModel!.payslip1;
    } else if (month === 2) {
      pslipModel = this.preauthModel!.payslip2;
    } else if (month === 3) {
      pslipModel = this.preauthModel!.payslip3;
    }

    this.lmsDialogHandler.openPayslipDialog(mnth, pslipModel, this.sallabel)
      ?.onClose.subscribe((pslip: PaySlipModel1) => {
        if (!pslip) {
          return;
        }

        pslip.month = month;
        pslip.otherIncome ||= 0;

        const currentDate = new Date();
        if (!pslip.date && !pslip.payslipDate) {
          pslip.date = currentDate;
          pslip.payslipDate = currentDate;
        } else if (pslip.date && !pslip.payslipDate) {
          pslip.payslipDate = typeof pslip.date === 'string' ? new Date(pslip.date) : pslip.date;
        } else if (!pslip.date && pslip.payslipDate) {
          pslip.date =
            typeof pslip.payslipDate === 'string' ? new Date(pslip.payslipDate) : pslip.payslipDate;
        }

        const nettValue = +pslip.nettIncome || 0;
        const otherValue = +pslip.otherIncome || 0;
        const grossValue = +pslip.grossIncome || nettValue;
        const totalIncome = nettValue;

        pslip.nettIncome = nettValue;
        pslip.otherIncome = otherValue;
        pslip.grossIncome = grossValue;

        if (month === 1) {
          this.preauthModel!.payslip1 = pslip;
          this.payslipControls.payslip1.patchValue(pslip, { onlySelf: true, emitEvent: true });
          this.income1 = totalIncome;
        } else if (month === 2) {
          this.preauthModel!.payslip2 = pslip;
          this.payslipControls.payslip2.patchValue(pslip, { onlySelf: true, emitEvent: true });
          this.income2 = totalIncome;
        } else if (month === 3) {
          this.preauthModel!.payslip3 = pslip;
          this.payslipControls.payslip3.patchValue(pslip, { onlySelf: true, emitEvent: true });
          this.income3 = totalIncome;
        }

        this.payslips.updateValueAndValidity();

        // Unified eventing path: monthly now also updates payslipsUpdated via this method.
        this.updatePayslipsArray();
      });
  }

  openWeeklyPaySlip(monthIndex: number, weekIndex: number) {
    console.log(`Opening weekly payslip for Month ${monthIndex}, Week ${weekIndex}`);

    const arrayIndex = (monthIndex - 1) * 4 + (weekIndex - 1);
    let existingPayslip = this.weeklyPayslips.find(
      (p) => p.month === monthIndex && (p as any).week === weekIndex,
    );

    if (!existingPayslip && this.weeklyIncomes[arrayIndex] > 0) {
      existingPayslip = {
        month: monthIndex,
        week: weekIndex,
        nettIncome: this.weeklyIncomes[arrayIndex],
        grossIncome: this.weeklyIncomes[arrayIndex],
        otherIncome: 0,
        date: new Date(),
        payslipDate: new Date(),
      } as any;
    }

    const pslipModel = existingPayslip ? { ...existingPayslip } : ({} as PaySlipModel1);
    pslipModel.month = monthIndex;
    (pslipModel as any).week = weekIndex;
    (pslipModel as any).incomeType = 'weekly';

    const dialogRef = this.lmsDialogHandler.openPayslipDialog(
      `${monthIndex}-${weekIndex}`,
      pslipModel,
      'Weekly',
    );

    dialogRef?.onClose.subscribe((pslip: PaySlipModel1) => {
      if (pslip) {
        pslip.otherIncome ||= 0;

        const nettValue = +pslip.nettIncome || 0;
        const grossValue = +pslip.grossIncome || nettValue;
        const otherValue = +pslip.otherIncome || 0;
        const totIn = nettValue + otherValue;

        if ((pslip as any).week === undefined) {
          (pslip as any).week = weekIndex;
        }
        (pslip as any).month = monthIndex;

        if (pslip.payslipDate) {
          pslip.date = pslip.payslipDate;
        } else if (pslip.date) {
          pslip.payslipDate = new Date(pslip.date);
        } else {
          const defaultDate = new Date();
          pslip.date = defaultDate;
          pslip.payslipDate = defaultDate;
        }

        pslip.nettIncome = nettValue;
        pslip.grossIncome = grossValue;

        this.weeklyIncomes[arrayIndex] = totIn;

        const existingIndex = this.weeklyPayslips.findIndex(
          (p) => p.month === monthIndex && (p as any).week === weekIndex,
        );

        if (existingIndex >= 0) {
          this.weeklyPayslips[existingIndex] = { ...pslip };
        } else {
          this.weeklyPayslips.push({ ...pslip });
        }

        console.log(
          `Updated weekly income at index ${arrayIndex}: Nett=${nettValue}, Gross=${grossValue}`,
        );
        this.updatePayslipsArray();
      }
    });
  }

  openFortnightlyPaySlip(monthIndex: number, fortnightIndex: number) {
    console.log(`Opening fortnightly payslip for Month ${monthIndex}, Fortnight ${fortnightIndex}`);

    const arrayIndex = (monthIndex - 1) * 2 + (fortnightIndex - 1);
    let existingPayslip = this.fortnightlyPayslips.find(
      (p) => p.month === monthIndex && (p as any).fortnight === fortnightIndex,
    );

    if (!existingPayslip && this.fortnightlyIncomes[arrayIndex] > 0) {
      existingPayslip = {
        month: monthIndex,
        fortnight: fortnightIndex,
        nettIncome: this.fortnightlyIncomes[arrayIndex],
        grossIncome: this.fortnightlyIncomes[arrayIndex],
        otherIncome: 0,
        date: new Date(),
        payslipDate: new Date(),
      } as any;
    }

    const pslipModel = existingPayslip ? { ...existingPayslip } : ({} as PaySlipModel1);
    pslipModel.month = monthIndex;
    (pslipModel as any).fortnight = fortnightIndex;
    (pslipModel as any).incomeType = 'fortnightly';

    const dialogRef = this.lmsDialogHandler.openPayslipDialog(
      `${monthIndex}-F${fortnightIndex}`,
      pslipModel,
      'Fortnightly',
    );

    dialogRef?.onClose.subscribe((pslip: PaySlipModel1) => {
      if (pslip) {
        pslip.otherIncome ||= 0;

        const nettValue = +pslip.nettIncome || 0;
        const grossValue = +pslip.grossIncome || nettValue;
        const otherValue = +pslip.otherIncome || 0;
        const totIn = nettValue + otherValue;

        if ((pslip as any).fortnight === undefined) {
          (pslip as any).fortnight = fortnightIndex;
        }
        (pslip as any).month = monthIndex;

        if (pslip.payslipDate) {
          pslip.date = pslip.payslipDate;
        } else if (pslip.date) {
          pslip.payslipDate = typeof pslip.date === 'string' ? new Date(pslip.date) : pslip.date;
        } else {
          const defaultDate = new Date();
          pslip.date = defaultDate;
          pslip.payslipDate = defaultDate;
        }

        pslip.nettIncome = nettValue;
        pslip.grossIncome = grossValue;

        this.fortnightlyIncomes[arrayIndex] = totIn;

        const existingIndex = this.fortnightlyPayslips.findIndex(
          (p) => p.month === monthIndex && (p as any).fortnight === fortnightIndex,
        );

        if (existingIndex >= 0) {
          this.fortnightlyPayslips[existingIndex] = { ...pslip };
        } else {
          this.fortnightlyPayslips.push({ ...pslip });
        }

        console.log(
          `Updated fortnightly income at index ${arrayIndex}: Nett=${nettValue}, Gross=${grossValue}`,
        );
        this.updatePayslipsArray();
      }
    });
  }

  private createSummaryPayslip(
    month: number,
    income: number,
    source: any,
    frequency: string,
  ): PaySlipModel1 {
    const rawDate = source?.payslipDate || source?.date || new Date();
    const parsedDate = typeof rawDate === 'string' ? new Date(rawDate) : rawDate;
    const safeDate = Number.isNaN(new Date(parsedDate).getTime())
      ? new Date()
      : new Date(parsedDate);

    return {
      ...(source || {}),
      month,
      nettIncome: income,
      grossIncome: income,
      otherIncome: 0,
      date: safeDate,
      payslipDate: safeDate,
      wageFrequency: frequency as any,
    } as PaySlipModel1;
  }

  private assignSummaryPayslipsFromFrequency(wageFreq: string): void {
    if (!this.preauthModel) {
      return;
    }

    (this.preauthModel as any).payslip1 = null;
    (this.preauthModel as any).payslip2 = null;
    (this.preauthModel as any).payslip3 = null;

    if (wageFreq === 'Weekly') {
      for (let month = 1; month <= 3; month++) {
        const avg = this.getWeeklyMonthTotal(month);
        if (avg <= 0) {
          continue;
        }
        const source = this.weeklyPayslips.find((p) => p.month === month);
        const summary = this.createSummaryPayslip(month, avg, source, 'Weekly');
        if (month === 1) this.preauthModel.payslip1 = summary;
        if (month === 2) this.preauthModel.payslip2 = summary;
        if (month === 3) this.preauthModel.payslip3 = summary;
      }
      return;
    }

    if (wageFreq === 'Fortnightly') {
      for (let month = 1; month <= 3; month++) {
        const avg = this.getFortnightlyMonthTotal(month);
        if (avg <= 0) {
          continue;
        }
        const source = this.fortnightlyPayslips.find((p) => p.month === month);
        const summary = this.createSummaryPayslip(month, avg, source, 'Fortnightly');
        if (month === 1) this.preauthModel.payslip1 = summary;
        if (month === 2) this.preauthModel.payslip2 = summary;
        if (month === 3) this.preauthModel.payslip3 = summary;
      }
      return;
    }

    const monthly = [
      this.preauthModel.payslip1,
      this.preauthModel.payslip2,
      this.preauthModel.payslip3,
    ];
    monthly.forEach((payslip, idx) => {
      if (!payslip) {
        return;
      }
      const income = (+payslip.nettIncome || 0) + (+payslip.otherIncome || 0);
      const summary = this.createSummaryPayslip(idx + 1, income, payslip, wageFreq);
      if (idx === 0) this.preauthModel!.payslip1 = summary;
      if (idx === 1) this.preauthModel!.payslip2 = summary;
      if (idx === 2) this.preauthModel!.payslip3 = summary;
    });
  }

  updatePayslipsArray() {
    if (!this.preauthModel) {
      return;
    }

    const wageFreq = this.f.wageFrequency.value;
    const payslipsArray: any[] = [];

    if (wageFreq === 'Weekly') {
      for (let month = 1; month <= 3; month++) {
        for (let week = 1; week <= 4; week++) {
          const storedPayslip = this.weeklyPayslips.find(
            (p) => p.month === month && (p as any).week === week,
          );

          if (storedPayslip && (+storedPayslip.nettIncome || 0) > 0) {
            const dateStr = this.global_utility.transformDate(
              storedPayslip.payslipDate || storedPayslip.date || new Date(),
            );

            payslipsArray.push({
              month,
              week,
              nettIncome: +storedPayslip.nettIncome || 0,
              grossIncome: +storedPayslip.grossIncome || +storedPayslip.nettIncome || 0,
              otherIncome: +storedPayslip.otherIncome || 0,
              date: dateStr,
              payslipDate: dateStr,
              wageFrequency: 'Weekly',
            });
          }
        }
      }

      // Backend summary slips = monthly averages from captured weekly UI slips.
      this.populateMonthlyPslip(payslipsArray, 4);

    } else if (wageFreq === 'Fortnightly') {
      for (let month = 1; month <= 3; month++) {
        for (let fortnight = 1; fortnight <= 2; fortnight++) {
          const storedPayslip = this.fortnightlyPayslips.find(
            (p) => p.month === month && (p as any).fortnight === fortnight,
          );

          if (storedPayslip && (+storedPayslip.nettIncome || 0) > 0) {
            const dateStr = this.global_utility.transformDate(
              storedPayslip.payslipDate || storedPayslip.date || new Date(),
            );

            payslipsArray.push({
              month,
              fortnight,
              nettIncome: +storedPayslip.nettIncome || 0,
              grossIncome: +storedPayslip.grossIncome || +storedPayslip.nettIncome || 0,
              otherIncome: +storedPayslip.otherIncome || 0,
              date: dateStr,
              payslipDate: dateStr,
              wageFrequency: 'Fortnightly',
            });
          }
        }
      }

      // Backend summary slips = monthly averages from captured fortnightly UI slips.
      this.populateMonthlyPslip(payslipsArray, 2);
    } else {
      // Monthly: preserve captured gross/nett/other from payslip1/2/3.
      const monthlySources = [
        this.preauthModel.payslip1,
        this.preauthModel.payslip2,
        this.preauthModel.payslip3,
      ];

      monthlySources.forEach((stored, idx) => {
        if (!stored) {
          return;
        }

        const nettIncome = +stored.nettIncome || 0;
        const grossIncome = +stored.grossIncome || nettIncome;
        const otherIncome = +stored.otherIncome || 0;

        if (nettIncome <= 0) {
          return;
        }
        const dateStr = this.global_utility.transformDate(
          stored.payslipDate || stored.date || new Date(),
        );

        payslipsArray.push({
          month: idx + 1,
          nettIncome,
          grossIncome,
          otherIncome,
          date: dateStr,
          payslipDate: dateStr,
          wageFrequency: wageFreq,
        });
      });
      // keep monthly slips as captured, only normalize month indexes
      //if (this.preauthModel.payslip1) this.preauthModel.payslip1.payslipDate = dateStr;
      //if (this.preauthModel.payslip2) this.preauthModel.payslip2.month = 2;
      //if (this.preauthModel.payslip3) this.preauthModel.payslip3.month = 3;
    }

    this.preauthModel.payslips = payslipsArray;
    this.preauthModel.wageCalendar = wageFreq;
    this.preauthModel.wageFrequency = wageFreq;
    const expectedCount = wageFreq === 'Weekly' ? 4 : wageFreq === 'Fortnightly' ? 2 : 1;
    this.populateMonthlyPslip(payslipsArray, expectedCount);
    this.payslipsUpdated.emit(this.preauthModel.payslips);
  }

  private populateMonthlyPslip(payslipsArray: any[], expectedCount: number) {
    if (expectedCount === 1) {
      this.paySlipCaptured.emit({ pslip: this.preauthModel!.payslip1, month: 1 });
      this.paySlipCaptured.emit({ pslip: this.preauthModel!.payslip2, month: 2 });
      this.paySlipCaptured.emit({ pslip: this.preauthModel!.payslip3, month: 3 });
      return; // No need to calculate averages for monthly payslips
    }
    const sortedPayslip = payslipsArray.sort(
      (a, b) => a.month - b.month || (a.fortnight || a.week || 0) - (b.fortnight || b.week || 0),
    );

    const month1count = sortedPayslip.filter((p) => p.month === 1).length || 0;
    const month2count = sortedPayslip.filter((p) => p.month === 2).length || 0;
    const month3count = sortedPayslip.filter((p) => p.month === 3).length || 0;

    if (
      month1count !== expectedCount ||
      month2count !== expectedCount ||
      month3count !== expectedCount
    ) {
      return;
    }

    const wageFrequency = expectedCount === 4 ? 'Weekly' : 'Fortnightly';

    this.preauthModel!.payslip1 = {
      ...(this.preauthModel!.payslip1 || {}),
      month: 1,
      nettIncome: sortedPayslip
        .filter((p) => p.month === 1)
        .reduce((sum, p) => sum + (+p.nettIncome || 0), 0),
      grossIncome: sortedPayslip
        .filter((p) => p.month === 1)
        .reduce((sum, p) => sum + (+p.grossIncome || 0), 0),
      otherIncome: 0,
      wageFrequency: wageFrequency as any,
      date: new Date(),
      payslipDate: new Date(),
    } as PaySlipModel1;

    this.preauthModel!.payslip2 = {
      ...(this.preauthModel!.payslip2 || {}),
      month: 2,
      nettIncome: sortedPayslip
        .filter((p) => p.month === 2)
        .reduce((sum, p) => sum + (+p.nettIncome || 0), 0),
      grossIncome: sortedPayslip
        .filter((p) => p.month === 2)
        .reduce((sum, p) => sum + (+p.grossIncome || 0), 0),
      otherIncome: 0,
      wageFrequency: wageFrequency as any,
      date: new Date(),
      payslipDate: new Date(),
    } as PaySlipModel1;

    this.preauthModel!.payslip3 = {
      ...(this.preauthModel!.payslip3 || {}),
      month: 3,
      nettIncome: sortedPayslip
        .filter((p) => p.month === 3)
        .reduce((sum, p) => sum + (+p.nettIncome || 0), 0),
      grossIncome: sortedPayslip
        .filter((p) => p.month === 3)
        .reduce((sum, p) => sum + (+p.grossIncome || 0), 0),
      otherIncome: 0,
      wageFrequency: wageFrequency as any,
      date: new Date(),
      payslipDate: new Date(),
    } as PaySlipModel1;

    if (expectedCount > 1) {
      this.paySlipCaptured.emit({ pslip: this.preauthModel!.payslip1, month: 1 });
      this.paySlipCaptured.emit({ pslip: this.preauthModel!.payslip2, month: 2 });
      this.paySlipCaptured.emit({ pslip: this.preauthModel!.payslip3, month: 3 });
    }
  }

  hasEmptyWeeklyIncomes(): boolean {
    return this.weeklyIncomes.some((inc) => inc === 0);
  }

  hasEmptyFortnightlyIncomes(): boolean {
    return this.fortnightlyIncomes.some((inc) => inc === 0);
  }

  hasEmptyMonthlyIncomes(): boolean {
    return this.income1 === 0 || this.income2 === 0 || this.income3 === 0;
  }

  shouldShowValidationError(): boolean {
    if (!this.Submitted) return false;

    if (this.isMonthlyClient) {
      return this.hasEmptyMonthlyIncomes();
    } else if (this.isWeeklyClient) {
      return this.hasEmptyWeeklyIncomes();
    } else if (this.isFortnightlyClient) {
      return this.hasEmptyFortnightlyIncomes();
    }

    return false;
  }

  updateAdditionalIncomeOptions(value) {
    const selectedValue = `${value ?? ''}`.trim().toLowerCase();
    this.isAINoneSelected = selectedValue.includes('none');
    this.isAIOtherOptionSelected = selectedValue.includes('other');

    if (this.isAINoneSelected) {
      this.preauthModel!.selectedadditionalIncome = 'None';
      this.isAIOtherOptionSelected = false;
      return;
    }
  }

  onAdditionalIncomeSelectionChange(value: any): void {
    this.updateAdditionalIncomeOptions(value);
  }

  ngOnDestroy() {
    this.additionalIncomeSub?.unsubscribe();
    this.employmentTypeSub?.unsubscribe();
    this.wageFrequencySub?.unsubscribe();
    this.parentFormGroup.removeControl(this.controlKey);
  }
}































